'use client';

import {
  type OneTrustEnvironment,
  OneTrustProvider,
} from '@jouzen/onetrust-consent-manager';
import type { MiddlewareFunction } from '@segment/analytics-next';
import { useFeatureGate } from '@statsig/react-bindings';
import { env } from 'next-runtime-env';
import { type ReactNode, useEffect } from 'react';

import { hipaaMiddleware } from '@/analytics/plugins/hipaaPlugin';
import { debugAnalytics } from '@/utils/debugAnalytics';

const HIPAA_MIDDLEWARE_GATE = 'enable_hipaa_middleware';

interface OneTrustEnvProps {
  environment?: OneTrustEnvironment;
  onetrustKey?: string;
  segmentKey?: string;
}

interface AnalyticsLike {
  _isOneTrustStub?: boolean;
  _oneTrustConsentManager?: boolean;
  instance?: unknown;
  addSourceMiddleware?: (fn: MiddlewareFunction) => unknown;
  // CSOL-187: idempotency marker so the registrar can't double-register under
  // React StrictMode (effects run twice) or parent re-mounts.
  _hipaaMiddlewareRegistered?: boolean;
}

function getMissingProps(props: OneTrustEnvProps): string[] {
  const missing: string[] = [];
  for (const [key, value] of Object.entries(props)) {
    if (value === undefined) {
      missing.push(key);
    }
  }
  return missing;
}

const POLL_MS = 100;
const POLL_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes — covers slow consent flows.

/**
 * Registers `hipaaMiddleware` as a Segment source middleware once
 * `window.analytics` is the loaded `AnalyticsBrowser` (rather than the
 * OneTrust pre-consent stub).
 *
 * Why polling instead of the `segmentPlugins` prop?
 *
 * `@jouzen/onetrust-consent-manager` (<= 2.6.0) deadlocks when
 * `segmentPlugins` is non-empty: its load IIFE does
 * `await a.register(...plugins); await a.load(...)` while the
 * `AnalyticsBrowser` is still pre-init. `register()` returns a Promise that
 * only resolves once `_preInitBuffer` flushes — and that flush requires
 * `a.load(...)` to fire, which the await is blocking. The result is no
 * `cdn.segment.com` request and every event stuck in `_preInitBuffer`.
 *
 * Why not the `useAnalyticsReady()` hook? It flips true only AFTER the
 * consent manager has already flushed its stub queue, so early events would
 * miss the middleware. Polling lets us register the moment the swap from
 * stub to real `AnalyticsBrowser` happens — before the queue flush.
 */
function HipaaMiddlewareRegistrar() {
  const { value: enabled } = useFeatureGate(HIPAA_MIDDLEWARE_GATE);

  useEffect(() => {
    if (!enabled) return;
    let registered = false;
    let elapsed = 0;

    const tryRegister = (): boolean => {
      if (registered) return true;
      const analytics = (window as unknown as { analytics?: AnalyticsLike })
        .analytics;
      if (
        !analytics ||
        analytics._isOneTrustStub ||
        typeof analytics.addSourceMiddleware !== 'function'
      ) {
        return false;
      }
      // Cross-mount idempotency: if another instance of this component has
      // already registered on this analytics instance, skip silently.
      if (analytics._hipaaMiddlewareRegistered) {
        registered = true;
        return true;
      }
      analytics.addSourceMiddleware(hipaaMiddleware);
      analytics._hipaaMiddlewareRegistered = true;
      registered = true;
      debugAnalytics('HipaaMiddlewareRegistrar: registered', {
        data: { elapsedMs: elapsed },
        logToDatadog: 'HipaaMiddlewareRegistrar',
      });
      return true;
    };

    if (tryRegister()) return;

    const intervalId = window.setInterval(() => {
      elapsed += POLL_MS;
      if (tryRegister()) {
        window.clearInterval(intervalId);
        return;
      }
      if (elapsed >= POLL_TIMEOUT_MS) {
        window.clearInterval(intervalId);
        debugAnalytics(
          'HipaaMiddlewareRegistrar: timed out waiting for analytics',
          {
            data: { elapsedMs: elapsed },
            level: 'error',
            logToDatadog: 'HipaaMiddlewareRegistrar',
          },
        );
      }
    }, POLL_MS);

    return () => window.clearInterval(intervalId);
  }, [enabled]);

  return null;
}

const OneTrustConsentProvider = ({
  children,
}: {
  readonly children: ReactNode;
}) => {
  const envProps: OneTrustEnvProps = {
    environment: env('NEXT_PUBLIC_OURA_ENV') as OneTrustEnvironment,
    segmentKey: env('NEXT_PUBLIC_SEGMENT_KEY'),
    onetrustKey: env('NEXT_PUBLIC_ONETRUST_DOMAIN_SCRIPT'),
  };

  const missingProps = getMissingProps(envProps);
  if (missingProps.length > 0) {
    debugAnalytics('Missing required environment variables', {
      data: {
        missingProperties: missingProps.join(', '),
        component: 'OneTrustConsentProvider',
      },
      level: 'error',
      logToDatadog: 'OneTrustConsentProvider',
    });
    return <>{children}</>;
  }

  return (
    <OneTrustProvider
      applicationName="ecom-order-portal"
      environment={envProps.environment!}
      onetrustKey={envProps.onetrustKey!}
      segmentKey={envProps.segmentKey!}
      enableEventQueue={true}
      onScriptLoad={() => {
        const analytics = (
          globalThis as unknown as { analytics?: AnalyticsLike }
        ).analytics;
        // `OneTrust ready` is a successful lifecycle signal, not an error.
        // It is intentionally NOT reported to Datadog: it fires on every page
        // load and previously inflated the `web_eop_analytics.error` metric
        // (which drives the error alert). Console diagnostics are retained when debug analytics is enabled.
        debugAnalytics('OneTrust ready', {
          data: {
            component: 'OneTrustConsentProvider',
            hasAnalytics: Boolean(analytics),
            isOneTrustConsentManager: analytics?._oneTrustConsentManager,
            isOneTrustStub: analytics?._isOneTrustStub,
            hasInstance: Boolean(analytics?.instance),
          },
        });
      }}
      onScriptError={(error: Error) =>
        debugAnalytics('OneTrust error', {
          data: { error, component: 'OneTrustConsentProvider' },
          level: 'error',
          exception: true,
          logToDatadog: 'OneTrustConsentProvider',
        })
      }
    >
      <HipaaMiddlewareRegistrar />
      {children}
    </OneTrustProvider>
  );
};

export default OneTrustConsentProvider;
